import type {Price} from '@/redux/v1/models';

import {useTheme} from '@emotion/react';
import {useBreakpointDown} from '@travel-frontend/core';
import {
  Checkbox as CheckboxUI,
  Stack,
  SPACING_XSMALL,
} from '@travel-frontend/ui-components';

import {HelpTooltip} from '@/components/HelpTooltip';
import {Label} from '@/modules/common/components/FilterCheckbox/components/Label';

type Props = {
  value: string;
  description?: string;
  label: string | JSX.Element;
  subLabel?: string;
  subLabelDescription?: string;
  count?: number;
  priceMin?: Price;
  active?: boolean;
  available?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  canShowOptionBestPrice?: boolean;
  canShowOptionCount: boolean;
  canShowOptionCountZero: boolean;
  testId?: string;
};

export const FilterCheckbox = ({
  value,
  description,
  count,
  label,
  active,
  available,
  priceMin,
  onChange,
  subLabel,
  subLabelDescription,
  canShowOptionBestPrice,
  canShowOptionCount,
  canShowOptionCountZero,
  testId,
}: Props) => {
  const theme = useTheme();
  const mdDown = useBreakpointDown('md');
  const hint = (
    <Stack
      direction="row"
      marginBottom="12px"
      alignItems="center"
      spacing={SPACING_XSMALL}
      color={available ? undefined : theme.colors.contentStateDisabled}
    >
      {subLabel}
      {subLabelDescription && (
        <HelpTooltip
          placement="top"
          content={subLabelDescription}
          escapeContent={false}
          style={{
            color: available ? undefined : theme.colors.contentStateDisabled,
            pointerEvents: available ? 'auto' : 'none',
          }}
        />
      )}
    </Stack>
  );

  return (
    <CheckboxUI
      size={mdDown ? 'medium' : 'small'}
      data-testid={testId}
      label={
        <Label
          label={label}
          canShowOptionCount={canShowOptionCount}
          count={count}
          canShowOptionCountZero={canShowOptionCountZero}
          canShowOptionBestPrice={canShowOptionBestPrice}
          optionValue={value}
          bestPrice={priceMin}
          description={description}
        />
      }
      subLabel={subLabel && hint}
      checked={active}
      disabled={!available}
      onChange={onChange}
      value={value}
    />
  );
};
